import { cx } from '@orn/shared-web/utils/cx';
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Modal as AriaModal,
  ModalOverlay as AriaModalOverlay,
} from 'react-aria-components';

import FullSidebarContent from './FullSidebarContent';

import type { NavItemType } from './config';
import type { LoggedInUser } from '../../providers/AuthProvider';

interface IMobileNavDrawerProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  user: LoggedInUser;
  activeUrl?: string;
  items: NavItemType[];
  footerItems?: NavItemType[];
  showAccountCard?: boolean;
  /** Forwarded to `FullSidebarContent`. See `MainSidebar` for the gate wiring. */
  showAssistantButton?: boolean;
  onOpenSearch: () => void;
}

/**
 * Left-sliding drawer shown below the `lg` breakpoint. Both the mobile top
 * bar's hamburger (`<md`) and the icon rail's top hamburger (`md`) open it,
 * so it is the unified "expand nav" surface for everything below the full
 * sidebar. Renders the same content as the desktop fixed sidebar. Built on
 * `react-aria-components` primitives — not on `SlideoutMenu` (right-slide +
 * brand header hardcoded in classnames).
 *
 * **Caller contract:** the overlay is `lg:hidden` so it is CSS-invisible at
 * `lg+`, but the underlying `react-aria` `Modal` mounts via a portal on
 * `document.body` and its `FocusScope` is JS-driven — `display:none` on the
 * portal root does not deactivate the focus trap. Callers must force-close
 * (`onOpenChange(false)`) when the viewport crosses to `lg+`, otherwise a
 * keyboard user can be silently trapped inside an invisible dialog.
 * `BaseSidebar` does this via `useBreakpoint('lg')`.
 */
const MobileNavDrawer = ({
  isOpen,
  onOpenChange,
  user,
  activeUrl,
  items,
  footerItems,
  showAccountCard,
  showAssistantButton,
  onOpenSearch,
}: IMobileNavDrawerProps) => {
  return (
    <AriaDialogTrigger isOpen={isOpen} onOpenChange={onOpenChange}>
      <AriaModalOverlay
        isDismissable
        className={(state) =>
          cx(
            'bg-overlay/70 outline-hidden fixed inset-0 z-50 flex min-h-dvh w-full items-stretch justify-start lg:hidden',
            state.isEntering && 'animate-in fade-in duration-300',
            state.isExiting && 'animate-out fade-out duration-200'
          )
        }
      >
        <AriaModal
          className={(state) =>
            cx(
              'bg-primary outline-hidden h-full w-80 max-w-[85%] shadow-xl',
              state.isEntering && 'animate-in slide-in-from-left duration-300',
              state.isExiting && 'animate-out slide-out-to-left duration-200'
            )
          }
        >
          <AriaDialog aria-label="Navigation" className="outline-hidden h-full w-full">
            <FullSidebarContent
              user={user}
              activeUrl={activeUrl}
              items={items}
              footerItems={footerItems}
              showAccountCard={showAccountCard}
              showAssistantButton={showAssistantButton}
              onOpenSearch={onOpenSearch}
            />
          </AriaDialog>
        </AriaModal>
      </AriaModalOverlay>
    </AriaDialogTrigger>
  );
};

export default MobileNavDrawer;
